import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  ExperienceTemplateStatus,
  IExperienceTemplate
} from '@dadohr/shared/domainTypes/experience';
import styled from '@emotion/styled';
import {
  Button,
  ButtonBase,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  Tooltip,
  Typography
} from '@material-ui/core';
import { Eye } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonWithPromise } from '../../../../../components/ButtonWithPromise';
import { DialogTitleWithTypography } from '../../../../../components/DialogTitleWithTypography';
import { RowsRendererScrollContainer } from '../../../../../components/GroupableList';
import { Loader } from '../../../../../components/Loader';
import { useDialogState } from '../../../../../hooks/useDialogState';
import { Section, SectionTitle } from '../../../../../layout/Section';
import { useRoutes } from '../../../../../routes';
import { useCurrentUser } from '../../../../../services/currentUser';
import { updateDoc } from '../../../../../services/firebase';
import { hasCurrentUserExperienceLevelScopes } from '../../../../../services/permissions';
import { now } from '../../../../../services/time';
import { AnchorField, useAnchorFields } from '../../../components/Anchors';
import { ExperiencePreviewV2Button } from '../../Details/ExperiencePreview';
import {
  EnrollmentPreviewTable,
  useMatchingUsersWithLaunchDates
} from '../EnrollmentPreview';
import {
  DiscardDraftOrCancelButton,
  FooterContainer,
  FooterContainerLeft,
  FooterContainerRight
} from '../Footer';
import { StepBasicsPreview } from '../StepBasics';
import { StepLaunchDatePreview } from '../StepLaunchDate';
import { StepParticipantsPreview } from '../StepParticipants';
import { StepTimelinePreview } from '../StepTimeline';
import { StepBodyProps } from '../types';

const ClickableSectionHeader = ({
  label,
  onClick
}: {
  label: React.ReactNode;
  onClick: () => void;
}) => (
  <ButtonBase onClick={onClick}>
    <Typography component="h1" variant="h4">
      {label}
    </Typography>
  </ButtonBase>
);

const SectionHeading = styled('div')((p) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  gap: p.theme.spacing()
}));

const HEIGHT = 300;

const EnrollmentDialog = ({
  open,
  onClose,
  d,
  onAfterConfirm,
  anchorFields
}: {
  open: boolean;
  onClose: () => void;
  d: Doc<IExperienceTemplate>;
  onAfterConfirm: () => Promise<any>;
  anchorFields: AnchorField[];
}) => {
  const { t } = useTranslation();
  const [ds, loading] = useMatchingUsersWithLaunchDates(d);

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitleWithTypography>
          {t('Start Experience')}?
        </DialogTitleWithTypography>
        <DialogContent>
          {loading && !ds && <Loader height={HEIGHT} />}
          {!loading && ds && (
            <>
              <div>
                <RowsRendererScrollContainer height={HEIGHT}>
                  <EnrollmentPreviewTable
                    d={d}
                    ds={ds}
                    anchorFields={anchorFields}
                  />
                </RowsRendererScrollContainer>
              </div>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button color="primary" onClick={onClose}>
            {t('Cancel')}
          </Button>
          <ButtonWithPromise
            variant="contained"
            color="primary"
            disabled={!ds}
            pending={`${t('Starting')}...`}
            onClick={async () => {
              if (!ds) {
                return;
              }
              //Implement new enrollUsers functionality
              await onAfterConfirm();
            }}
          >
            {t('Start Experience')}
          </ButtonWithPromise>
        </DialogActions>
      </Dialog>
    </>
  );
};

export const StepReview = ({
  d,
  label,
  goTo: goToStep
}: StepBodyProps<Doc<IExperienceTemplate>>) => {
  const currentUser = useCurrentUser();
  const { id: updatedBy, org } = currentUser;
  const anchorFields = useAnchorFields(org.customUserFields);
  const { t } = useTranslation();
  const { ROUTES, goTo } = useRoutes();

  const { dialogOpen, closeDialog } = useDialogState(false);

  const activate = () =>
    updateDoc(d, (x) => ({
      ...x,
      status: ExperienceTemplateStatus.ACTIVE,
      updatedBy,
      updatedAt: now()
    })).then(() => goTo(ROUTES.experiences.details.general.url(d.id)));

  const containerStyle = {
    marginBottom: 16
  };

  const canLaunch = hasCurrentUserExperienceLevelScopes(currentUser, d.id, [
    'launch'
  ]);

  return (
    <>
      <div style={containerStyle}>
        <Grid container justify="space-between">
          <Grid item xs>
            <Typography component="h1" variant="h3">
              {label}
            </Typography>
          </Grid>
          <Grid item>
            <ExperiencePreviewV2Button
              variant="outlined"
              color="primary"
              startIcon={<Eye size={16} />}
              d={d}
            >
              {t('Preview experience')}
            </ExperiencePreviewV2Button>
          </Grid>
        </Grid>
      </div>
      <Section>
        <SectionTitle>
          <ClickableSectionHeader
            label={t('Basics')}
            onClick={() => goToStep('basics')}
          />
        </SectionTitle>
        <StepBasicsPreview d={d} />
      </Section>
      <Section>
        <Grid container xs={12} spacing={2}>
          <Grid item xs={12} md={6}>
            <SectionTitle>
              <ClickableSectionHeader
                label={t('Start and End')}
                onClick={() => goToStep('startAndEnd')}
              />
            </SectionTitle>
            <StepLaunchDatePreview d={d} />
          </Grid>
          <Grid item xs={12} md={6}>
            <SectionTitle>
              <ClickableSectionHeader
                label={t('Auto-enrollment')}
                onClick={() => goToStep('participants')}
              />
            </SectionTitle>
            <StepParticipantsPreview d={d} />
          </Grid>
        </Grid>
      </Section>
      <Section>
        <SectionHeading>
          <SectionTitle>
            <ClickableSectionHeader
              label={t('Timeline')}
              onClick={() => goToStep('tasks')}
            />
          </SectionTitle>
        </SectionHeading>
        <StepTimelinePreview d={d} />
      </Section>
      <FooterContainer>
        <FooterContainerLeft>
          <DiscardDraftOrCancelButton d={d} />
        </FooterContainerLeft>
        <FooterContainerRight>
          <ButtonWithPromise
            color="primary"
            pending={`${t('Saving')}...`}
            variant={
              d.data.status === ExperienceTemplateStatus.DRAFT ||
              d.data.status === ExperienceTemplateStatus.ROUGH_DRAFT
                ? 'text'
                : 'contained'
            }
            onClick={() =>
              updateDoc(d, (t) => t).then(() =>
                goTo(ROUTES.experiences.overview.url())
              )
            }
          >
            {t('Save and exit')}
          </ButtonWithPromise>
          {d.data.status === ExperienceTemplateStatus.DRAFT &&
            (canLaunch ? (
              <ButtonWithPromise
                variant="contained"
                color="primary"
                pending={`${t('Starting')}...`}
                onClick={async () =>
                  // d.data.enrollRule ? openDialog() : activate()
                  activate()
                }
              >
                {t('Launch experience now')}
              </ButtonWithPromise>
            ) : (
              <Tooltip
                title={`${t(
                  'Please contact the account owner to start this experience'
                )}`}
              >
                <span>
                  <Button disabled={true}>{t('Launch experience now')}</Button>
                </span>
              </Tooltip>
            ))}
        </FooterContainerRight>
      </FooterContainer>
      {dialogOpen && (
        <EnrollmentDialog
          open={true}
          onClose={closeDialog}
          d={d}
          onAfterConfirm={activate}
          anchorFields={anchorFields}
        />
      )}
    </>
  );
};
